/**
 * The two things a panel shows before it can show itself: it is still asking, or it could not.
 *
 * Neither existed. Every list screen rendered its Empty — "No open missions", "The card bank is
 * empty", "Nothing yet" — from the first frame, before a single request had resolved, and again
 * whenever one came back 500. Those are three different situations wearing one face, and the one
 * they wore was the reassuring one: a game telling somebody there is nothing for them today when
 * in fact it never managed to ask.
 *
 * So: skeletons while the first attempt is in flight, and a card that says so plainly when it
 * failed with nothing to fall back on. `Skeleton` has been in `components/ui` unused since the
 * migration; this is what it was copied in for.
 */
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { cn } from '@/lib/utils';
import * as React from 'react';
import { View } from 'react-native';

/**
 * One card's worth of "something is coming": the band, then two lines of it.
 *
 * Shaped like the card it stands in for rather than a spinner, because the point is to hold the
 * layout still — a panel that jumps as its content arrives is a panel that looked broken for the
 * half second before it did.
 */
function SkeletonCard({ band }: { band?: boolean }) {
  return (
    <Card>
      <CardContent className="gap-3">
        {band ? <Skeleton className="bg-muted -mx-4 -mt-4 mb-1 h-11 rounded-t-[9px]" /> : null}
        <Skeleton className="h-4 w-2/3" />
        <Skeleton className="h-4 w-full" />
      </CardContent>
    </Card>
  );
}

/**
 * What a panel renders until its first load settles.
 *
 * Two or three cards and no more: this is a placeholder for a beat, and a screen of grey blocks
 * is its own kind of noise. The first one wears a band because most of these panels open on a
 * titled card, so the shape that arrives is the shape that was being held.
 */
export function PanelSkeleton({ rows = 2, className }: { rows?: number; className?: string }) {
  return (
    <View className={cn('gap-3', className)} accessibilityLabel="Loading">
      {Array.from({ length: rows }, (_, i) => (
        <SkeletonCard key={i} band={i === 0} />
      ))}
    </View>
  );
}

/**
 * The load failed and there is nothing behind it.
 *
 * One sentence and one way out. It does not name the status code or the endpoint: what somebody
 * can do about it is press the button, and everything else is a detail for a log. The reason is
 * carried quietly underneath for the case where the server did say something useful.
 */
export function LoadFailed({ onRetry, reason }: { onRetry: () => void; reason?: string | null }) {
  return (
    <Card>
      <CardContent className="gap-3">
        <Text variant="heading">Couldn’t load this panel.</Text>
        <Text className="text-muted-foreground">
          Nothing is lost — the server just did not answer. Try again in a moment.
        </Text>
        {reason ? <Text variant="note">{reason}</Text> : null}
        <Button variant="outline" className="self-start" onPress={onRetry}>
          <Text>Try again</Text>
        </Button>
      </CardContent>
    </Card>
  );
}
